import React, { useMemo } from 'react';
import { useLeadFlowOptional } from '../../providers/LeadFlowProvider';
import type { SubmittedResultSummary } from '../../providers/LeadFlowProvider';
import type { Result } from '../../types/result';
import { useActionDispatcher } from '../../utils/action-resolver/ActionDispatcher';
import ComparisonBanner from '../ComparisonBanner';
import ComparisonModal from '.';

/**
 * Wires the comparison modal's program CTA to the governed conversion
 * drawer. Clicking a compared program's CTA hands the selected result to
 * `LeadFlowProvider.openLeadFlow`, which opens the platform-owned
 * `SingleSubmissionDrawer`.
 *
 * Reads the lead flow optionally so the modal still wires when no
 * `LeadFlowProvider` is in scope: `onProductClick` degrades to
 * `undefined` (the modal renders its CTA inert) rather than throwing.
 * Comparison-enabled result configs always mount one.
 *
 * Returns the bound handler (or `undefined`) and the governed submitted
 * results so a single hook owns the wiring both render sites
 * (`ConfigurableStep` and `DslConfig`) share — re-divergence across the
 * two surfaces is structurally impossible.
 */
export function useComparisonProductClick(): {
  readonly onProductClick:
    | ((result: Result, skipDuplicateCheck?: boolean) => void)
    | undefined;
  readonly submittedResults: SubmittedResultSummary[] | undefined;
} {
  const leadFlow = useLeadFlowOptional();
  const openLeadFlow = leadFlow?.openLeadFlow;
  const dispatcher = useActionDispatcher();

  // When a governed lead flow is in scope (result pages always mount one)
  // the modal CTA hands the program to `openLeadFlow`. Surfaces without a
  // lead flow — e.g. an Editorial Listing entry-point config, which links
  // out instead of converting in-page — fall back to the program's
  // resolved application CTA URL. Offerings only resolves that field for
  // offerings with an active budget/campaign `application_cta` link
  // (a narrower set than "has a linkout at all"), so when it's absent this
  // falls through to the same `navigation.linkout` BFF redirect the
  // in-card "Visit Site" CTA already uses for the identical offering —
  // otherwise the modal CTA would close the modal and silently no-op.
  const onProductClick = useMemo(
    () =>
      openLeadFlow
        ? (result: Result, skipDuplicateCheck = false) =>
            openLeadFlow(result, { skipDuplicateCheck })
        : (result: Result) => {
            const url = result.applicationCtaURL;
            if (url && typeof window !== 'undefined') {
              window.open(url, '_blank', 'noopener,noreferrer');
              return;
            }
            if (result.offeringId && dispatcher.has?.('navigation.linkout')) {
              dispatcher.dispatch('navigation.linkout', {
                offeringId: result.offeringId,
              });
            }
          },
    [openLeadFlow, dispatcher],
  );

  return {
    onProductClick,
    submittedResults: leadFlow?.submittedResults,
  };
}

/**
 * Shared comparison surface — the `ComparisonBanner` + `ComparisonModal`
 * pair, wired to the governed lead flow through
 * {@link useComparisonProductClick}. Mounted inside a `ComparisonProvider`
 * by both `ConfigurableStep` (bridge render path) and `DslConfig`
 * (standalone render path) so the CTA wiring stays identical across them.
 */
const ComparisonRuntime: React.FC = () => {
  const { onProductClick, submittedResults } = useComparisonProductClick();

  return (
    <>
      <ComparisonBanner />
      <ComparisonModal
        squareButtons
        onProductClick={onProductClick}
        submittedResults={submittedResults}
      />
    </>
  );
};

export default ComparisonRuntime;
